import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, TeamIdx, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';

// D124 — the free-kick wall.
//
// D88 shipped a wall as three lines of inline heuristic inside `actPlayer`: the three nearest
// defenders, evenly spread on the ball→goal line, with the size and the trigger distance as literals.
// The user's report ("free kicks need a defence wall so they feel proper — and the defending team can
// move it") asks for two things that heuristic cannot express: a wall whose SIZE and PLACEMENT read
// like football, and a way for a human to influence it.
//
// This is pure AI-layer geometry: bots read a plan out of `MatchState` and steer to their slot.
// `enforceRestartClearance` remains the legality guarantee, so a badly-placed wall is still legal.
// No new sim state, no wire change, no rng — the plan is a deterministic function of the state, and
// ties break on EntityId so two identical states always produce the same wall.

export interface WallSlot {
  readonly entity: EntityId;
  readonly spot: Vec2;
}

export interface WallPlan {
  /** Where the middle of the wall stands (already near-post biased / anchor-dragged). */
  readonly center: Vec2;
  readonly slots: readonly WallSlot[];
  /** A defending human who joined the line and is dragging it, if any. */
  readonly anchor: EntityId | null;
}

/** How many men the distance ladder wants, capped by what this team size can spare. */
export function wallSizeFor(distToGoal: number, teamSize: number, cfg: SimConfig): number {
  const w = cfg.ai.WALL;
  const pitch = PITCH[teamSize as keyof typeof PITCH];
  const frac = distToGoal / pitch.length;
  // SIZES is an ascending ladder of distance fractions; the first band it falls in wants the most men.
  let want = 0;
  for (let i = 0; i < w.SIZES.length; i++) {
    if (frac <= w.SIZES[i]!) {
      want = w.SIZES.length - i; // nearest band ⇒ 3, then 2, then 1
      break;
    }
  }
  return Math.min(want, w.MAX[teamSize] ?? 0);
}

/**
 * The wall for a pending free kick against `defTeam`, or null when there should be no wall (too far
 * out, wrong restart kind, or the team size cannot spare anyone).
 *
 * `humanHeld` names entities a human is driving: they are never conscripted into the wall (you do not
 * get marched into a line you did not choose), but a defending human who WALKS onto the line becomes
 * its anchor and drags the bot slots with him — the "defending team can move the wall" mechanic, with
 * no new input bit and no new state, because the bots simply read his position out of the state.
 */
export function wallPlan(state: MatchState, defTeam: TeamIdx, cfg: SimConfig, humanHeld: ReadonlySet<EntityId> = new Set()): WallPlan | null {
  const r = state.restart;
  if (r === null || r.kind !== 'FREE_KICK' || r.team === defTeam) return null;

  const w = cfg.ai.WALL;
  const pitch = PITCH[state.mode.teamSize];
  const ownGoal = { x: (-state.attackDir[defTeam] * pitch.length) / 2, y: 0 };
  const ball = state.ball.pos;
  const toGoal = { x: ownGoal.x - ball.x, y: ownGoal.y - ball.y };
  const gl = Math.hypot(toGoal.x, toGoal.y) || 1;
  if (gl > pitch.length * w.MAX_DIST_FRAC) return null; // a wall is for shooting range, not midfield

  const size = wallSizeFor(gl, state.mode.teamSize, cfg);
  if (size <= 0) return null;

  const ux = toGoal.x / gl;
  const uy = toGoal.y / gl;
  const px = -uy; // perpendicular to ball→goal: the line the wall stands on
  const py = ux;
  const ring = pitch.restartClearance + 0.3; // just outside the sim's clearance ring, so it stays legal
  const pitchGap = 2 * cfg.collision.CAPSULE_R + w.GAP;
  const width = (size - 1) * pitchGap;

  // Near-post bias: shade the wall toward the post the ball can actually see, so the keeper only has to
  // cover the far side — the single most football-legible thing a wall does.
  const nearPost = { x: ownGoal.x, y: ball.y >= 0 ? pitch.goalWidth / 2 : -pitch.goalWidth / 2 };
  const along = (nearPost.x - ball.x) * px + (nearPost.y - ball.y) * py; // ball→near-post, along the wall line
  const biasSign = along >= 0 ? 1 : -1;
  let center: Vec2 = {
    x: ball.x + ux * ring + px * biasSign * width * w.POST_BIAS,
    y: ball.y + uy * ring + py * biasSign * width * w.POST_BIAS,
  };

  // Candidates: own-team outfielders who are not being driven by a human, nearest to the line first,
  // EntityId breaking ties so the same state always builds the same wall.
  const candidates = state.players
    .filter((q) => q.team === defTeam && !q.isGk && !q.sentOff && !humanHeld.has(q.id))
    .sort((a, b) => {
      const da = Math.hypot(a.pos.x - center.x, a.pos.y - center.y);
      const db = Math.hypot(b.pos.x - center.x, b.pos.y - center.y);
      return da === db ? a.id - b.id : da - db;
    });

  // Join-and-drag: a defending HUMAN standing on the wall line anchors it. The bots re-centre on him,
  // so strafing sideways walks the whole wall across the goal mouth. Lowest EntityId wins if two join.
  let anchor: EntityId | null = null;
  const joiners = state.players
    .filter((q) => q.team === defTeam && !q.isGk && !q.sentOff && humanHeld.has(q.id))
    .filter((q) => Math.hypot(q.pos.x - center.x, q.pos.y - center.y) <= w.JOIN_RADIUS + width / 2)
    .sort((a, b) => a.id - b.id);
  const joined = joiners[0];
  if (joined) {
    anchor = joined.id;
    // keep the wall ON its ring (his distance from the ball is his business) but take his lateral offset
    const rel = (joined.pos.x - ball.x) * px + (joined.pos.y - ball.y) * py;
    center = { x: ball.x + ux * ring + px * rel, y: ball.y + uy * ring + py * rel };
  }

  const members = candidates.slice(0, size);
  const slots = members.map((q, i) => {
    const off = (i - (members.length - 1) / 2) * pitchGap;
    return { entity: q.id, spot: { x: center.x + px * off, y: center.y + py * off } };
  });
  return { center, slots, anchor };
}

/** This player's wall slot, or null if he is not in the wall. Convenience for the bot decision. */
export function wallSpotFor(state: MatchState, p: PlayerState, cfg: SimConfig, humanHeld?: ReadonlySet<EntityId>): Vec2 | null {
  const plan = wallPlan(state, p.team, cfg, humanHeld);
  if (plan === null) return null;
  return plan.slots.find((s) => s.entity === p.id)?.spot ?? null;
}
